import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/colors';
import { useAuth } from '../contexts/AuthContext';
import { Guest, AdminStats, CreateGuestData, UpdateGuestData, apiClient, handleApiError, RSVPData, RSVPEventId, ApiError } from '../services/api';
import { Link } from 'react-router-dom';

const RSVP_EVENT_LABELS: { id: RSVPEventId; label: string }[] = [
  { id: 'welcomeDrinks', label: 'Welcome' },
  { id: 'poolParty', label: 'Pool' },
  { id: 'rehearsalDinner', label: 'Dinner' },
  { id: 'ceremony', label: 'Ceremony' }
];

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const AdminContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #dee2e6;
  
  @media (max-width: 768px) {
    margin-bottom: 0.5rem;
    padding: 0.5rem;
    border-radius: 4px;
  }
`;

const Title = styled.h1`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 2rem;
  font-weight: 600;
  color: #212529;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  color: #6c757d;
  margin: 0;
  font-size: 1rem;
  font-weight: 400;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.25rem;
    margin-bottom: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  border: 1px solid #dee2e6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.25rem;
    border-radius: 4px;
  }
`;

const StatNumber = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #0056b3;
  margin-bottom: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 0.125rem;
  }
`;

const StatLabel = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  color: #212529;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.125rem;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    line-height: 1.2;
  }
`;

const StatSubtext = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  color: #6c757d;
  font-size: 0.75rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MainContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  border: 1px solid #dee2e6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 4px;
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    margin-bottom: 0.75rem;
    gap: 0.5rem;
  }
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  flex: 1;
  max-width: 400px;
  background: white;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0056b3;
    box-shadow: 0 0 0 3px rgba(0, 86, 179, 0.1);
  }

  &::placeholder {
    color: #6c757d;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 16px; /* Prevent iOS zoom */
    padding: 0.5rem 0.75rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    justify-content: center;
    gap: 0.25rem;
  }
`;

const Button = styled.button`
  background: #0056b3;
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #004494;
  }

  &.secondary {
    background: white;
    color: #495057;
    border: 1px solid #ced4da;

    &:hover {
      background: #e9ecef;
      border-color: #adb5bd;
    }
  }

  &.danger {
    background: #dc3545;

    &:hover {
      background: #c82333;
    }
  }
  
  &.desktop-only {
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const TableContainer = styled.div`
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dee2e6;
  
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 4px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  background: white;
  
  @media (max-width: 768px) {
    min-width: 100%; /* Force table to fit screen width */
    font-size: 0.7rem;
    table-layout: fixed; /* Force equal distribution */
    
    /* Make columns fit on screen */
    td:nth-child(1), th:nth-child(1) { width: 30%; } /* Name */
    td:nth-child(2), th:nth-child(2) { width: 15%; }  /* RSVP */
    td:nth-child(3), th:nth-child(3) { width: 15%; }  /* Contact */
    td:nth-child(4), th:nth-child(4) { width: 15%; } /* Attendance */
    td:nth-child(6), th:nth-child(6) { width: 15%; }  /* Actions */
  }
`;

const TableHeader = styled.th`
  background: #f8f9fa;
  color: #495057;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  border-bottom: 2px solid #dee2e6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &.mobile-hidden {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.1rem;
    font-size: 0.55rem;
    line-height: 1;
    text-align: center;
    word-break: break-word;
    
    &:first-child {
      text-align: left;
      padding-left: 0.2rem;
    }
  }
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f8f9fa;
  }

  &:hover {
    background: #e9ecef;
  }

  transition: background-color 0.1s ease;
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
  font-size: 0.9rem;
  
  &.mobile-hidden {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .mobile-only {
    display: none;
    
    @media (max-width: 768px) {
      display: flex !important;
      justify-content: center;
      align-items: center;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.1rem;
    font-size: 0.65rem;
    text-align: center;
    vertical-align: middle;
    word-break: break-word;
    
    &:first-child {
      text-align: left;
      padding-left: 0.2rem;
    }
  }
`;

const PartyName = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #212529;
  margin-bottom: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 0.125rem;
    word-wrap: break-word;
    line-height: 1.2;
    text-align: left;
  }
`;

const PartyMembersList = styled.div`
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const StatusBadge = styled.span<{ status: 'responded' | 'pending' | 'attending' | 'not-attending' }>`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  display: inline-block;
  margin: 0.125rem 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  
  @media (max-width: 768px) {
    padding: 0.15rem 0.3rem;
    font-size: 0.6rem;
    letter-spacing: 0.2px;
  }
  
  ${props => {
    switch (props.status) {
      case 'responded':
        return `background: #d4edda; color: #155724; border: 1px solid #c3e6cb;`;
      case 'pending':
        return `background: #fff3cd; color: #856404; border: 1px solid #ffeaa7;`;
      case 'attending':
        return `background: #d4edda; color: #155724; border: 1px solid #c3e6cb;`;
      case 'not-attending':
        return `background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb;`;
      default:
        return `background: #e9ecef; color: #495057; border: 1px solid #dee2e6;`;
    }
  }}
`;

const AttendanceDetails = styled.div`
  margin-top: 0.5rem;
`;

const AttendingCount = styled.div`
  font-weight: 600;
  color: #212529;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
`;

const ContactInfo = styled.div`
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.4;
`;

const ContactLine = styled.div`
  margin-bottom: 0.125rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactLabel = styled.span`
  font-weight: 600;
  color: #495057;
  margin-right: 0.25rem;
`;

const NoContactInfo = styled.div`
  font-size: 0.8rem;
  color: #adb5bd;
  font-style: italic;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  margin: 1rem 0;
  text-align: center;
  padding: 1rem;
  background: #f8d7da;
  border-radius: 6px;
  border: 1px solid #f5c6cb;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  color: #6c757d;
  font-size: 1rem;
`;

const LoginForm = styled.form`
  max-width: 400px;
  margin: 0 auto;
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
  margin-bottom: 0.5rem;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  background: white;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0056b3;
    box-shadow: 0 0 0 3px rgba(0, 86, 179, 0.1);
  }

  &::placeholder {
    color: #6c757d;
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent iOS zoom */
  }
`;

const LoginButton = styled(Button)`
  width: 100%;
  margin-top: 0.5rem;
`;

// Modal components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  
  @media (max-width: 768px) {
    padding: 1rem;
    width: 95%;
    max-width: none;
    margin: 0.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
`;

const ModalTitle = styled.h2`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: #212529;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldLabel = styled.label`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
`;

const TextInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0056b3;
    box-shadow: 0 0 0 3px rgba(0, 86, 179, 0.1);
  }

  &::placeholder {
    color: #6c757d;
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent iOS zoom */
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  font-size: 0.9rem;
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0056b3;
    box-shadow: 0 0 0 3px rgba(0, 86, 179, 0.1);
  }

  &::placeholder {
    color: #6c757d;
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent iOS zoom */
  }
`;

const NameTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const NameTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  color: #495057;
`;

const RemoveNameButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 0.7rem;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: #c82333;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
`;

const MobileAddButton = styled(Button)`
  .desktop-text {
    display: inline;
  }
  .mobile-text {
    display: none;
  }
  
  @media (max-width: 768px) {
    padding: 0;
    font-size: 1rem;
    min-width: auto;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .desktop-text {
      display: none;
    }
    .mobile-text {
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }
  }
`;

const MobileStatusBadge = styled(StatusBadge)`
  @media (max-width: 768px) {
    padding: 0.15rem 0.3rem;
    font-size: 0.6rem;
    border-radius: 4px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    min-width: 1.2rem;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    text-align: center;
  }
`;

const DesktopStatus = styled.div`
  display: block;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileAttendance = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    color: #495057;
  }
`;

const DesktopAttendance = styled.div`
  display: block;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileActions = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
  }
`;

const DesktopActions = styled.div`
  display: block;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DeleteButton = styled(Button)`
  @media (max-width: 768px) {
    padding: 0.25rem;
    font-size: 0.8rem;
    min-width: 1.5rem;
    height: 1.5rem;
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const CountdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: row;
    gap: 0.5rem;
    justify-content: center;
  }
`;

const CountdownNumber = styled(StatNumber)`
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 0;
  }
`;

const CountdownLabel = styled(StatLabel)`
  @media (max-width: 768px) {
    font-size: 0.7rem;
    margin-bottom: 0;
  }
`;

const ConfirmDialog = styled.div`
  text-align: center;
  
  p {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    color: #495057;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }
  
  .party-name {
    font-weight: 600;
    color: #212529;
  }
`;

const AdminPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, error: authError, userType, login, logout, checkAuth } = useAuth();
  const [parties, setParties] = useState<Guest[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [adminLoginError, setAdminLoginError] = useState<string | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);
  const [deletingGuest, setDeletingGuest] = useState<Guest | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    partyName: '',
    searchableNames: [] as string[],
    pseudonyms: [] as string[],
    newName: '',
    newPseudonym: '',
    rsvpAttendance: '' as '' | 'yes' | 'no',
    rsvpPartyMembers: [] as { name: string; attending: boolean }[],
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [attendanceFilter, setAttendanceFilter] = useState<'all' | 'yes' | 'no'>('all');

  // Countdown to 12/05/26
  const calculateDaysLeft = () => {
    const now = new Date();
    const target = new Date('2026-12-05T00:00:00');
    const diffMs = target.getTime() - now.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };
  const [daysLeft, setDaysLeft] = useState<number>(calculateDaysLeft());
  useEffect(() => {
    const interval = setInterval(() => {
      setDaysLeft(calculateDaysLeft());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Removed redundant checkAuth() call - now handled by AuthProvider

  useEffect(() => {
    if (isAuthenticated && userType === 'admin') {
      loadAdminData();
    } else if (isAuthenticated && userType) {
      setAdminLoginError('Admin access required. Please enter the admin password.');
      logout();
    }
  }, [isAuthenticated, userType]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    
    setLoginLoading(true);
    setAdminLoginError(null); // Clear any previous admin error
    
    const success = await login(password);
    setLoginLoading(false);
    
    if (success) {
      setPassword('');
      setAdminLoginError(null);
    } else {
      // Set admin-specific error message
      setAdminLoginError('Invalid admin password. Please try again.');
    }
  };

  const loadAdminData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load both guests and stats in parallel
      const [guestsData, statsData] = await Promise.all([
        apiClient.getAllGuestsWithContactInfo(),
        apiClient.getAdminStats()
      ]);
      
      setParties(guestsData);
      setStats(statsData);
    } catch (error) {
      console.log('loadAdminData error:', error);
      // Check if this is an authentication error
      if (handleAuthError(error)) {
        console.log('Authentication error detected, logging out...');
        return;
      }
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to handle authentication errors consistently
  const handleAuthError = (error: unknown): boolean => {
    console.log('handleAuthError called with:', error);
    if (error instanceof ApiError) {
      console.log('Error is ApiError with status:', error.status, 'message:', error.message);
      // Check for 401/403 status codes or admin permission errors
      if (error.status === 401 || error.status === 403 || 
          error.message.includes('insufficient permissions') ||
          error.message.includes('Admin access required')) {
        console.log('Authentication/permission error detected');
        
        // Set admin-specific error message and logout
        setAdminLoginError('Admin access required. Please enter the admin password.');
        logout();
        return true; // Indicates auth error was handled
      }
    }
    console.log('Not an auth error');
    return false; // Not an auth error
  };

  // Modal and form handlers
  const openAddModal = () => {
    setFormData({ partyName: '', searchableNames: [], pseudonyms: [], newName: '', newPseudonym: '', rsvpAttendance: '', rsvpPartyMembers: [] });
    setFormError(null);
    setShowAddModal(true);
  };

  const openEditModal = (guest: Guest) => {
    setEditingGuest(guest);
    setFormData({
      partyName: guest.fullName,
      searchableNames: [...guest.searchableNames],
      pseudonyms: [...(guest.pseudonyms || [])],
      newName: '',
      newPseudonym: '',
      rsvpAttendance: guest.rsvp?.attendance || '',
      rsvpPartyMembers: guest.rsvp?.partyMembers && guest.rsvp.partyMembers.length > 0
        ? guest.rsvp.partyMembers.map(pm => ({ name: pm.name, attending: pm.attending }))
        : guest.searchableNames.map(name => ({ name, attending: false })),
    });
    setFormError(null);
    setShowEditModal(true);
  };

  const openDeleteModal = (guest: Guest) => {
    setDeletingGuest(guest);
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setEditingGuest(null);
    setDeletingGuest(null);
    setFormData({ partyName: '', searchableNames: [], pseudonyms: [], newName: '', newPseudonym: '', rsvpAttendance: '', rsvpPartyMembers: [] });
    setFormError(null);
  };

  const handleAddName = () => {
    const trimmedName = formData.newName.trim();
    console.log('Adding name:', { 
      originalName: formData.newName, 
      trimmedName, 
      currentSearchableNames: formData.searchableNames 
    });
    
    if (trimmedName && !formData.searchableNames.includes(trimmedName)) {
      setFormData(prev => ({
        ...prev,
        searchableNames: [...prev.searchableNames, trimmedName],
        rsvpPartyMembers: [...prev.rsvpPartyMembers, { name: trimmedName, attending: false }],
        newName: ''
      }));
      console.log('Name added successfully');
    } else {
      console.log('Name not added - either empty or duplicate');
    }
  };

  const handleRemoveName = (nameToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      searchableNames: prev.searchableNames.filter(name => name !== nameToRemove),
      rsvpPartyMembers: prev.rsvpPartyMembers.filter(member => member.name !== nameToRemove),
    }));
  };

  const handleAddPseudonym = () => {
    const trimmedPseudonym = formData.newPseudonym.trim();
    const existingPseudonyms = formData.pseudonyms.map(name => name.toLowerCase());

    if (trimmedPseudonym && !existingPseudonyms.includes(trimmedPseudonym.toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        pseudonyms: [...prev.pseudonyms, trimmedPseudonym],
        newPseudonym: ''
      }));
    }
  };

  const handleRemovePseudonym = (pseudonymToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      pseudonyms: prev.pseudonyms.filter(pseudonym => pseudonym !== pseudonymToRemove),
    }));
  };

  const handleCreateGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Debug logging
    console.log('Form submission data:', {
      partyName: formData.partyName,
      partyNameTrimmed: formData.partyName.trim(),
      searchableNames: formData.searchableNames,
      searchableNamesLength: formData.searchableNames.length
    });
    
    if (!formData.partyName.trim() || formData.searchableNames.length === 0) {
      setFormError('Party name and at least one guest name are required');
      return;
    }

    setFormLoading(true);
    setFormError(null);

    try {
      const newGuest = await apiClient.createGuest({
        partyName: formData.partyName.trim(),
        searchableNames: formData.searchableNames,
        pseudonyms: formData.pseudonyms
      });

      setParties(prev => [...prev, newGuest]);
      closeModals();
      // Refresh stats
      loadAdminData();
    } catch (error) {
      console.error('Error creating guest:', error);
      if (handleAuthError(error)) {
        return;
      }
      setFormError(handleApiError(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGuest || !formData.partyName.trim() || formData.searchableNames.length === 0) {
      setFormError('Party name and at least one guest name are required');
      return;
    }

    setFormLoading(true);
    setFormError(null);

    try {
      // Update guest base data
      const updatedGuest = await apiClient.updateGuest(editingGuest.id, {
        partyName: formData.partyName.trim(),
        searchableNames: formData.searchableNames,
        pseudonyms: formData.pseudonyms
      });

      // Optionally update RSVP if attendance has been selected
      if (formData.rsvpAttendance) {
        const ceremonyPartyMembers = formData.rsvpAttendance === 'yes' ? formData.rsvpPartyMembers : [];
        const eventResponses = editingGuest.rsvp?.eventResponses
          ? {
              ...editingGuest.rsvp.eventResponses,
              ceremony: {
                partyMembers: ceremonyPartyMembers
              }
            }
          : undefined;

        const rsvpData: RSVPData = {
          guestId: editingGuest.id,
          attendance: formData.rsvpAttendance,
          partyMembers: ceremonyPartyMembers,
          eventResponses,
          message: ''
        };

        if (editingGuest.rsvp?.id) {
          await apiClient.updateRSVP(editingGuest.rsvp.id, rsvpData);
        } else {
          await apiClient.submitRSVP(rsvpData);
        }
      }

      setParties(prev => prev.map(party => 
        party.id === editingGuest.id ? updatedGuest : party
      ));
      closeModals();
      // Refresh stats and guest list
      loadAdminData();
    } catch (error) {
      if (handleAuthError(error)) {
        return;
      }
      setFormError(handleApiError(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteGuest = async () => {
    if (!deletingGuest) return;

    setFormLoading(true);
    setFormError(null);

    try {
      await apiClient.deleteGuest(deletingGuest.id);
      setParties(prev => prev.filter(party => party.id !== deletingGuest.id));
      closeModals();
      // Refresh stats
      loadAdminData();
    } catch (error) {
      if (handleAuthError(error)) {
        return;
      }
      setFormError(handleApiError(error));
    } finally {
      setFormLoading(false);
    }
  };

  const filteredParties = parties
    .filter(party =>
      party.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      party.searchableNames.some(name => 
        name.toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      (party.pseudonyms || []).some(pseudonym =>
        pseudonym.toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
    .filter(party => {
      if (attendanceFilter === 'all') return true;
      if (attendanceFilter === 'yes') return party.rsvp?.attendance === 'yes';
      return party.rsvp?.attendance === 'no';
    });

  const getAttendanceStatus = (party: Guest) => {
    if (!party.hasResponded) return 'pending';
    if (party.rsvp?.attendance === 'yes') return 'attending';
    return 'not-attending';
  };

  const getEventAttendees = (party: Guest, eventId: RSVPEventId) => {
    const eventMembers = party.rsvp?.eventResponses?.[eventId]?.partyMembers;

    if (eventMembers) {
      return eventMembers.filter(member => member.attending);
    }

    return eventId === 'ceremony' && party.rsvp?.partyMembers
      ? party.rsvp.partyMembers.filter(member => member.attending)
      : [];
  };

  // Use stats from API if available, otherwise calculate from parties data
  const totalParties = stats?.totalParties ?? parties.length;
  const respondedParties = stats?.respondedParties ?? parties.filter(p => p.hasResponded).length;
  const totalGuests = stats?.totalGuests ?? parties.reduce((sum, party) => sum + party.searchableNames.length, 0);
  const attendingGuests = stats?.attendingGuests ?? parties.reduce((sum, party) => {
    if (party.rsvp?.partyMembers) {
      return sum + party.rsvp.partyMembers.filter(member => member.attending).length;
    }
    return sum;
  }, 0);
  const responseRate = stats?.responseRate ?? (totalParties > 0 ? Math.round((respondedParties / totalParties) * 100) : 0);

  if (!isAuthenticated || userType !== 'admin') {
    return (
      <PageContainer>
        <AdminContainer>
          <Header>
            <Title>Admin Login</Title>
            <Subtitle>Enter the admin password to access the dashboard</Subtitle>
          </Header>

          <MainContent>
            <LoginForm onSubmit={handleLogin}>
              <FormGroup>
                <Label htmlFor="password">Admin Password</Label>
                <PasswordInput
                  id="password"
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setAdminLoginError(null); // Clear error when user types
                  }}
                  disabled={loginLoading || authLoading}
                />
              </FormGroup>
              
              {adminLoginError && <ErrorMessage>{adminLoginError}</ErrorMessage>}
              
              <LoginButton 
                type="submit" 
                disabled={!password.trim() || loginLoading || authLoading}
              >
                {loginLoading || authLoading ? 'Signing in...' : 'Sign In'}
              </LoginButton>
            </LoginForm>
          </MainContent>
        </AdminContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <AdminContainer>
        <Header>
          <CountdownContainer>
            <CountdownNumber>{daysLeft}</CountdownNumber>
            <CountdownLabel>Days until 12/05/26</CountdownLabel>
          </CountdownContainer>
        </Header>

        <StatsGrid>
          <StatCard>
            <StatNumber>{totalGuests}</StatNumber>
            <StatLabel>Total Invited</StatLabel>
            <StatSubtext>guests invited</StatSubtext>
          </StatCard>
          <StatCard onClick={() => setAttendanceFilter(prev => (prev === 'yes' ? 'all' : 'yes'))} style={{ cursor: 'pointer', outline: attendanceFilter === 'yes' ? '2px solid #0056b3' : 'none' }}>
            <StatNumber>{attendingGuests}</StatNumber>
            <StatLabel>Confirmed Attending</StatLabel>
            <StatSubtext>guests confirmed</StatSubtext>
          </StatCard>
          <StatCard>
            <StatNumber>{responseRate}%</StatNumber>
            <StatLabel>Response Rate</StatLabel>
            <StatSubtext>{respondedParties} of {totalParties} responded</StatSubtext>
          </StatCard>
        </StatsGrid>

        <MainContent>
          <ActionBar>
            <SearchInput
              type="text"
              placeholder="Search parties or guests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <ButtonGroup>
              <MobileAddButton onClick={openAddModal}>
                <span className="desktop-text">Add New Party</span>
                <span className="mobile-text">+</span>
              </MobileAddButton>
              <Button className="secondary desktop-only">Export Guest List</Button>
            </ButtonGroup>
          </ActionBar>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {loading ? (
            <LoadingMessage>Loading your guest list...</LoadingMessage>
          ) : (
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>RSVP</TableHeader>
                    <TableHeader>Contact</TableHeader>
                    <TableHeader>Attend</TableHeader>
                    <TableHeader className="mobile-hidden">Response Date</TableHeader>
                    <TableHeader>Del</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredParties.map(party => (
                    <TableRow key={party.id} onClick={() => openEditModal(party)} style={{ cursor: 'pointer' }}>
                      <TableCell>
                        <PartyName>{party.fullName}</PartyName>
                        <PartyMembersList>
                          {party.searchableNames.join(' • ')}
                          {(party.pseudonyms || []).length > 0 && (
                            <>
                              <br />
                              Aliases: {(party.pseudonyms || []).join(' • ')}
                            </>
                          )}
                        </PartyMembersList>
                      </TableCell>
                      <TableCell>
                        <DesktopStatus>
                          {!party.hasResponded && (
                          <StatusBadge status={party.hasResponded ? 'responded' : 'pending'}>
                            Pending
                          </StatusBadge>)}
                          {party.hasResponded && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <StatusBadge
                                status={getAttendanceStatus(party)}
                                onClick={(e: React.MouseEvent) => { e.stopPropagation(); setAttendanceFilter(party.rsvp?.attendance === 'yes' ? 'yes' : 'no'); }}
                                style={{ cursor: 'pointer' }}
                              >
                                {party.rsvp?.attendance === 'yes' ? 'Attending' : 'Not Attending'}
                              </StatusBadge>
                            </div>
                          )}
                        </DesktopStatus>
                        <div className="mobile-only" style={{ display: 'none' }}>
                          {!party.hasResponded ? (
                            <MobileStatusBadge status="pending">?</MobileStatusBadge>
                          ) : (
                            <MobileStatusBadge 
                              status={getAttendanceStatus(party)}
                              onClick={(e: React.MouseEvent) => { e.stopPropagation(); setAttendanceFilter(party.rsvp?.attendance === 'yes' ? 'yes' : 'no'); }}
                              style={{ cursor: 'pointer' }}
                            >
                              {party.rsvp?.attendance === 'yes' ? 'Y' : 'N'}
                            </MobileStatusBadge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {party.contactInfo && (
                          party.contactInfo.email || 
                          party.contactInfo.mobile || 
                          party.contactInfo.streetAddress || 
                          party.contactInfo.city
                        ) ? (
                          <StatusBadge status="responded">Yes</StatusBadge>
                        ) : (
                          <StatusBadge status="pending">No</StatusBadge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DesktopAttendance>
                          {party.hasResponded ? (
                            <AttendanceDetails>
                              {RSVP_EVENT_LABELS.map(event => {
                                const attendees = getEventAttendees(party, event.id);

                                return (
                                  <div key={event.id} style={{ marginBottom: '0.35rem' }}>
                                    <AttendingCount>
                                      {event.label}: {attendees.length} of {party.searchableNames.length}
                                    </AttendingCount>
                                    {attendees.length > 0 && (
                                      <PartyMembersList>
                                        {attendees.map(member => member.name).join(' • ')}
                                      </PartyMembersList>
                                    )}
                                  </div>
                                );
                              })}
                            </AttendanceDetails>
                          ) : (
                            <PartyMembersList>Awaiting response</PartyMembersList>
                          )}
                        </DesktopAttendance>
                        <MobileAttendance>
                          {!party.hasResponded ? '...' : `${getEventAttendees(party, 'ceremony').length}/${party.searchableNames.length}`}
                        </MobileAttendance>
                      </TableCell>
                      <TableCell className="mobile-hidden">
                        {party.rsvp?.submittedAt ? (
                          <div>
                            {new Date(party.rsvp.submittedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        ) : (
                          <PartyMembersList>—</PartyMembersList>
                        )}
                      </TableCell>
                      <TableCell>
                        <DesktopActions>
                          <ButtonGroup>
                            <Button 
                              className="secondary" 
                              style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }} 
                              onClick={(e: React.MouseEvent) => { e.stopPropagation(); openEditModal(party); }}
                            >
                              Edit
                            </Button>
                            <Button 
                              className="danger" 
                              style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }} 
                              onClick={(e: React.MouseEvent) => { e.stopPropagation(); openDeleteModal(party); }}
                            >
                              Remove
                            </Button>
                          </ButtonGroup>
                        </DesktopActions>
                        <MobileActions>
                          <DeleteButton 
                            className="danger" 
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); openDeleteModal(party); }}
                            title="Delete party"
                          >
                            ×
                          </DeleteButton>
                        </MobileActions>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          )}

          {filteredParties.length === 0 && !loading && (
            <LoadingMessage>
              {searchTerm ? `No parties found matching "${searchTerm}"` : 'No parties in your guest list yet'}
            </LoadingMessage>
          )}
        </MainContent>
      </AdminContainer>

      {showAddModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Add New Party</ModalTitle>
              <CloseButton onClick={closeModals}>&times;</CloseButton>
            </ModalHeader>
            <Form onSubmit={handleCreateGuest}>
              <FormField>
                <FieldLabel htmlFor="add-party-name">Party Name</FieldLabel>
                <TextInput
                  id="add-party-name"
                  type="text"
                  placeholder="e.g., The Smiths"
                  value={formData.partyName}
                  onChange={(e) => setFormData(prev => ({ ...prev, partyName: e.target.value }))}
                  required
                  disabled={formLoading}
                />
              </FormField>
              <FormField>
                <FieldLabel>Guest Names</FieldLabel>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <TextInput
                    type="text"
                    placeholder="Enter a guest name"
                    value={formData.newName}
                    onChange={(e) => setFormData(prev => ({ ...prev, newName: e.target.value }))}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddName();
                      }
                    }}
                    disabled={formLoading}
                    style={{ flex: 1 }}
                  />
                  <Button 
                    type="button" 
                    onClick={handleAddName}
                    disabled={formLoading || !formData.newName.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Add Guest
                  </Button>
                </div>
                <NameTagsContainer>
                  {formData.searchableNames.map((name, index) => (
                    <NameTag key={index}>
                      {name}
                      <RemoveNameButton type="button" onClick={() => handleRemoveName(name)}>&times;</RemoveNameButton>
                    </NameTag>
                  ))}
                </NameTagsContainer>
              </FormField>
              <FormField>
                <FieldLabel>Pseudonyms / Search Aliases</FieldLabel>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <TextInput
                    type="text"
                    placeholder="e.g., Nick for Nicholas"
                    value={formData.newPseudonym}
                    onChange={(e) => setFormData(prev => ({ ...prev, newPseudonym: e.target.value }))}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPseudonym();
                      }
                    }}
                    disabled={formLoading}
                    style={{ flex: 1 }}
                  />
                  <Button 
                    type="button" 
                    onClick={handleAddPseudonym}
                    disabled={formLoading || !formData.newPseudonym.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Add Alias
                  </Button>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                  Aliases help RSVP lookup only. They do not count as invited guests.
                </div>
                <NameTagsContainer>
                  {formData.pseudonyms.map((pseudonym, index) => (
                    <NameTag key={index}>
                      {pseudonym}
                      <RemoveNameButton type="button" onClick={() => handleRemovePseudonym(pseudonym)}>&times;</RemoveNameButton>
                    </NameTag>
                  ))}
                </NameTagsContainer>
              </FormField>
              {formError && <ErrorMessage>{formError}</ErrorMessage>}
              <ModalActions>
                <Button className="secondary" onClick={closeModals} disabled={formLoading}>Cancel</Button>
                <Button type="submit" disabled={formLoading}>
                  {formLoading ? 'Adding...' : 'Add Party'}
                </Button>
              </ModalActions>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {showEditModal && editingGuest && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Edit Party</ModalTitle>
              <CloseButton onClick={closeModals}>&times;</CloseButton>
            </ModalHeader>
            <Form onSubmit={handleUpdateGuest}>
              <FormField>
                <FieldLabel htmlFor="edit-party-name">Party Name</FieldLabel>
                <TextInput
                  id="edit-party-name"
                  type="text"
                  placeholder="e.g., The Smiths"
                  value={formData.partyName}
                  onChange={(e) => setFormData(prev => ({ ...prev, partyName: e.target.value }))}
                  required
                  disabled={formLoading}
                />
              </FormField>

              {/* Contact Information Display */}
              {editingGuest.contactInfo && (
                editingGuest.contactInfo.email || 
                editingGuest.contactInfo.mobile || 
                editingGuest.contactInfo.streetAddress || 
                editingGuest.contactInfo.city
              ) && (
                <FormField>
                  <FieldLabel>Contact Information</FieldLabel>
                  <ContactInfo style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                    {editingGuest.contactInfo.email && (
                      <ContactLine>
                        <ContactLabel>Email:</ContactLabel>
                        {editingGuest.contactInfo.email}
                      </ContactLine>
                    )}
                    {editingGuest.contactInfo.mobile && (
                      <ContactLine>
                        <ContactLabel>Phone:</ContactLabel>
                        {editingGuest.contactInfo.mobile}
                      </ContactLine>
                    )}
                    {(editingGuest.contactInfo.streetAddress || editingGuest.contactInfo.city || editingGuest.contactInfo.state) && (
                      <ContactLine>
                        <ContactLabel>Address:</ContactLabel>
                        {[
                          editingGuest.contactInfo.streetAddress,
                          editingGuest.contactInfo.apt && `Apt ${editingGuest.contactInfo.apt}`,
                          editingGuest.contactInfo.city,
                          editingGuest.contactInfo.state,
                          editingGuest.contactInfo.zip,
                          editingGuest.contactInfo.country && editingGuest.contactInfo.country !== 'US' ? editingGuest.contactInfo.country : null
                        ].filter(Boolean).join(', ')}
                      </ContactLine>
                    )}
                  </ContactInfo>
                </FormField>
              )}

              {/* Invite Link Section */}
              {editingGuest.inviteToken && editingGuest.inviteToken.trim() && (
                <FormField>
                  <FieldLabel>Invite Link</FieldLabel>
                  <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={`https://gremery.com/address?token=${editingGuest.inviteToken}`}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                          background: 'white'
                        }}
                      />
                      <Button 
                        type="button"
                        className="secondary"
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                        onClick={() => {
                          navigator.clipboard.writeText(`https://gremery.com/address?token=${editingGuest.inviteToken}`);
                          // You could add a toast notification here
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                      Share this link with the party to access their RSVP page
                    </div>
                  </div>
                </FormField>
              )}

              <FormField>
                <FieldLabel>Guest Names</FieldLabel>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <TextInput
                    type="text"
                    placeholder="Enter a guest name"
                    value={formData.newName}
                    onChange={(e) => setFormData(prev => ({ ...prev, newName: e.target.value }))}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddName();
                      }
                    }}
                    disabled={formLoading}
                    style={{ flex: 1 }}
                  />
                  <Button 
                    type="button" 
                    onClick={handleAddName}
                    disabled={formLoading || !formData.newName.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Add Guest
                  </Button>
                </div>
                <NameTagsContainer>
                  {formData.searchableNames.map((name, index) => (
                    <NameTag key={index}>
                      {name}
                      <RemoveNameButton type="button" onClick={() => handleRemoveName(name)}>&times;</RemoveNameButton>
                    </NameTag>
                  ))}
                </NameTagsContainer>
              </FormField>

              <FormField>
                <FieldLabel>Pseudonyms / Search Aliases</FieldLabel>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <TextInput
                    type="text"
                    placeholder="e.g., Nick for Nicholas"
                    value={formData.newPseudonym}
                    onChange={(e) => setFormData(prev => ({ ...prev, newPseudonym: e.target.value }))}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPseudonym();
                      }
                    }}
                    disabled={formLoading}
                    style={{ flex: 1 }}
                  />
                  <Button 
                    type="button" 
                    onClick={handleAddPseudonym}
                    disabled={formLoading || !formData.newPseudonym.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Add Alias
                  </Button>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                  Aliases help RSVP lookup only. They do not count as invited guests.
                </div>
                <NameTagsContainer>
                  {formData.pseudonyms.map((pseudonym, index) => (
                    <NameTag key={index}>
                      {pseudonym}
                      <RemoveNameButton type="button" onClick={() => handleRemovePseudonym(pseudonym)}>&times;</RemoveNameButton>
                    </NameTag>
                  ))}
                </NameTagsContainer>
              </FormField>

              {/* RSVP Editing */}
              <FormField>
                <FieldLabel>RSVP Attendance</FieldLabel>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="rsvpAttendance"
                      value="yes"
                      checked={formData.rsvpAttendance === 'yes'}
                      onChange={() => setFormData(prev => ({ ...prev, rsvpAttendance: 'yes' }))}
                      disabled={formLoading}
                    />
                    Yes
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="radio"
                      name="rsvpAttendance"
                      value="no"
                      checked={formData.rsvpAttendance === 'no'}
                      onChange={() => setFormData(prev => ({ ...prev, rsvpAttendance: 'no' }))}
                      disabled={formLoading}
                    />
                    No
                  </label>
                </div>
              </FormField>

              {formData.rsvpAttendance === 'yes' && (
                <FormField>
                  <FieldLabel>Who is attending?</FieldLabel>
                  <div>
                    {formData.rsvpPartyMembers.map((member, index) => (
                      <label key={member.name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={member.attending}
                          onChange={(e) => {
                            const updated = [...formData.rsvpPartyMembers];
                            updated[index] = { ...updated[index], attending: e.target.checked };
                            setFormData(prev => ({ ...prev, rsvpPartyMembers: updated }));
                          }}
                          disabled={formLoading}
                        />
                        {member.name}
                      </label>
                    ))}
                  </div>
                </FormField>
              )}

              {formError && <ErrorMessage>{formError}</ErrorMessage>}
              <ModalActions>
                <Button className="secondary" onClick={closeModals} disabled={formLoading}>Cancel</Button>
                <Button type="submit" disabled={formLoading}>
                  {formLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </ModalActions>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {showDeleteModal && deletingGuest && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Confirm Deletion</ModalTitle>
              <CloseButton onClick={closeModals}>&times;</CloseButton>
            </ModalHeader>
            <ConfirmDialog>
              <p>Are you sure you want to delete the party <span className="party-name">{deletingGuest.fullName}</span>?</p>
              <p>This action cannot be undone.</p>
              {formError && <ErrorMessage>{formError}</ErrorMessage>}
              <ModalActions>
                <Button className="secondary" onClick={closeModals} disabled={formLoading}>Cancel</Button>
                <Button className="danger" onClick={handleDeleteGuest} disabled={formLoading}>
                  {formLoading ? 'Deleting...' : 'Delete Party'}
                </Button>
              </ModalActions>
            </ConfirmDialog>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default AdminPage; 